import type { PricingCardProps } from "./PricingCard";

export type PricingPlan = "free" | "individual" | "studio" | "enterprise";

type PricingCardConfig = Omit<PricingCardProps, "ctaSlot" | "className" | "children">;

export const PRICING_CONFIGS: Record<PricingPlan, PricingCardConfig> = {
  free: {
    planName: "Free",
    planBadgeVariant: "default",
    price: "$0",
    pricePeriod: "/month",
    features: [
      {
        category: "Projects",
        items: [{ text: "2 projects" }, { text: "macOS, iPadOS, Windows and visionOS" }],
      },
      {
        category: "Storage",
        items: [{ text: "Cloud storage" }],
      },
      {
        category: "Import",
        items: [{ text: "XT, STEP, IGES, DWG, STL" }, { text: "SOLIDWORKS" }, { text: "Images" }],
      },
      {
        category: "Export",
        items: [{ text: "Basic quality 3MF, STL" }],
      },
      {
        category: "Generative Tools",
        items: [{ text: "20 generations/month" }],
      },
      {
        category: "Support & Training",
        items: [{ text: "Basic technical support" }],
      },
    ],
  },

  individual: {
    planName: "Solo",
    planBadgeVariant: "blue",
    price: "$32.99",
    pricePeriod: "/month",
    subtitle: "$249.99 Billed annually, excluding VAT",
    tagline: "Everything in Free plus",
    highlighted: true,
    features: [
      {
        category: "Projects",
        items: [{ text: "100 projects" }, { text: "Version retention up to 1 year" }],
      },
      {
        category: "Storage",
        items: [{ text: "Centralized cloud storage" }],
      },
      {
        category: "Import",
        items: [{ text: "XT, STEP, IGES, DWG, STL" }, { text: "SOLIDWORKS" }, { text: "Images" }],
      },
      {
        category: "Export",
        items: [
          { text: "Shapr3D" },
          { text: "High quality 3MF, STL" },
          { text: "STEP, IGES" },
          { text: "DXF, DWG, SVG, PDF" },
          { text: "USDZ, GLB, OBJ" },
        ],
      },
      {
        category: "Generative Tools",
        items: [{ text: "100 generations/month" }],
      },
      {
        category: "Collaboration & Documentation",
        items: [
          { text: "Technical drawings for straightforward manufacturing hand-offs" },
          { text: "Real-time renders with 100+ materials" },
          { text: "Integrated AR on iPad for instant digital prototyping" },
          {
            text: "Secure links for sharing 3D models, AR preview, and technical drawings",
          },
        ],
      },
      {
        category: "Support & Training",
        items: [{ text: "Priority technical support" }],
      },
    ],
    seatTypes: [{ label: "2 Contributor seats", color: "orange" }],
  },

  studio: {
    planName: "Studio",
    planBadgeVariant: "default",
    price: "$53.99",
    pricePeriod: "/month",
    priceSubline: "per Editor",
    subtitle: "$449.99 Billed annually, excluding VAT",
    tagline: "Everything in Solo plus",
    features: [
      {
        category: "Projects",
        items: [
          { text: "Unlimited projects" },
          { text: "Unlimited teamspaces" },
          { text: "Version retention up to 3 years" },
        ],
      },
      {
        category: "Storage",
        items: [{ text: "Company ownership of all data" }, { text: "Centralized cloud storage" }],
      },
      {
        category: "Import",
        items: [
          { text: "USDC, JT" },
          { text: "Catia (V5), NX, Creo, Solid Edge" },
          { text: "Images" },
        ],
      },
      {
        category: "Export",
        items: [{ text: "JT" }, { text: "X_T, X_B" }],
      },
      {
        category: "Generative Tools",
        items: [{ text: "1000 generations/month" }],
      },
      {
        category: "Collaboration & Documentation",
        items: [
          { text: "Custom material libraries for consistent brand presentations" },
          { text: "Vision Pro for individual, immersive design sessions" },
        ],
      },
      {
        category: "Support & Training",
        items: [{ text: "Priority technical support" }],
      },
    ],
    seatTypes: [
      { label: "Up to 10 Editor seats", color: "blue" },
      { label: "2 Contributor seats", color: "orange" },
    ],
  },

  enterprise: {
    planName: "Enterprise",
    planBadgeVariant: "dark",
    price: "Contact Sales",
    pricePeriod: "",
    tagline: "Everything in Studio plus",
    features: [
      {
        category: "Projects",
        items: [
          { text: "Unlimited projects", icon: "dot" },
          { text: "Unlimited teams and teamspaces", icon: "dot" },
          { text: "Unlimited version retention", icon: "dot" },
        ],
      },
      {
        category: "Storage",
        items: [
          { text: "Company ownership of all data", icon: "dot" },
          {
            text: "Flexible data storage options:",
            icon: "dot",
            subItems: ["Single tenant", "Private cloud", "On-premises", "Cloud off"],
          },
        ],
      },
      {
        category: "Import",
        items: [{ text: "Custom import options", icon: "dot" }],
      },
      {
        category: "Export",
        items: [{ text: "Custom export options", icon: "dot" }],
      },
      {
        category: "Generative Tools",
        items: [{ text: "Unlimited generations", icon: "dot" }],
      },
      {
        category: "Collaboration & Documentation",
        items: [
          {
            text: "Vision Pro for immersive design reviews in XR–even remotely",
            icon: "dot",
          },
        ],
      },
      {
        category: "Support & Training",
        items: [
          {
            text: "Accelerated onboarding & dedicated account manager",
            icon: "dot",
          },
        ],
      },
      {
        category: "Security & Compliance",
        items: [{ text: "ISO 27001 & SOC 2", icon: "dot" }],
      },
    ],
    seatTypes: [
      { label: "Unlimited Editor seats", color: "blue" },
      { label: "Seat manager", color: "orange" },
    ],
  },
};
